import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Briefcase,
  Calendar,
  ClipboardList,
  Package,
  TrendingUp,
  CheckSquare,
  FileText,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRolePreview } from "@/contexts/RolePreviewContext";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";

type AppRole = "PM" | "Field Lead" | "Procurement" | "Viewer" | "Admin" | "Full" | "Crew" | "Owner";

interface MainAction {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  description: string;
  color: string;
  visibleTo: "all" | "crew-only" | "management" | "pm-owner-admin";
  featureKey?: string;
}

export function HomeMobileQuickNav() {
  const navigate = useNavigate();
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const { previewRole, isInPreviewMode } = useRolePreview();
  const { isFeatureEnabled } = useFeatureFlags();

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (roles) {
          setUserRoles(roles.map((r) => r.role as AppRole));
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRoles();
  }, []);

  const effectiveRoles: AppRole[] = isInPreviewMode && previewRole
    ? [previewRole as AppRole]
    : userRoles;

  const isCrewOnly = effectiveRoles.length > 0 &&
    effectiveRoles.every((role) => role === "Crew" || role === "Field Lead" || role === "Viewer");
  const isPMOwnerAdmin = effectiveRoles.some((role) => ["PM", "Owner", "Admin"].includes(role));

  const allActions: MainAction[] = [
    {
      title: "Today Board",
      icon: CheckSquare,
      path: "/today-board",
      description: "Daily tasks and priorities",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "all",
    },
    {
      title: "Job Dashboard",
      icon: Briefcase,
      path: "/jobs",
      description: "All active projects",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "all",
    },
    {
      title: "Schedule",
      icon: Calendar,
      path: "/schedule",
      description: "Crew and timeline",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "all",
    },
    {
      title: "Calendar",
      icon: Calendar,
      path: "/calendar",
      description: "Events and meetings",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "all",
    },
    {
      title: "Daily Log",
      icon: FileText,
      path: "/daily-log",
      description: "Log your work",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "crew-only",
      featureKey: "daily_log",
    },
    {
      title: "Procurement",
      icon: Package,
      path: "/procurement",
      description: "Materials and orders",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "management",
      featureKey: "procurement",
    },
    {
      title: "Field QA Dashboard",
      icon: ClipboardList,
      path: "/field-qa",
      description: "Quality tracking",
      color: "bg-primary hover:bg-primary/90",
      visibleTo: "pm-owner-admin",
      featureKey: "field_qa",
    },
    {
      title: "Job KPIs",
      icon: TrendingUp,
      path: "/kpi-dashboard",
      description: "Company metrics",
      color: "bg-accent hover:bg-accent/90",
      visibleTo: "pm-owner-admin",
      featureKey: "job_kpis",
    },
  ];

  const filteredActions = allActions.filter((action) => {
    let visible = false;
    if (action.visibleTo === "all") visible = true;
    else if (action.visibleTo === "crew-only") visible = isCrewOnly;
    else if (action.visibleTo === "management") visible = !isCrewOnly;
    else if (action.visibleTo === "pm-owner-admin") visible = isPMOwnerAdmin;

    if (!visible) return false;
    if (action.featureKey && !isFeatureEnabled(action.featureKey)) return false;
    return true;
  });

  // Role-aware ordering: surface the most relevant action first
  const priorityOrder: string[] = isPMOwnerAdmin
    ? ["/today-board", "/jobs", "/schedule", "/field-qa", "/calendar", "/procurement", "/kpi-dashboard"]
    : isCrewOnly
    ? ["/today-board", "/daily-log", "/schedule", "/jobs", "/calendar"]
    : ["/today-board", "/jobs", "/schedule", "/calendar"];

  const mainActions = [...filteredActions].sort((a, b) => {
    const ai = priorityOrder.indexOf(a.path);
    const bi = priorityOrder.indexOf(b.path);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const getTourAttribute = (path: string) => {
    if (path === "/today-board") return "today-card";
    if (path === "/jobs") return "jobs-card";
    if (path === "/schedule") return "schedule-card";
    return undefined;
  };

  return (
    <div className="md:hidden">
      {loading ? (
        <div className="grid grid-cols-2 gap-3 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-border bg-card/80 backdrop-blur-sm">
              <CardContent className="p-4 flex flex-col items-center gap-2">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-8">
          {mainActions.map((action) => {
            const Icon = action.icon;
            const tourAttr = getTourAttribute(action.path);

            return (
              <Card
                key={action.title}
                className="group cursor-pointer transition-all duration-300 active:scale-95 hover:shadow-xl border-border bg-card/80 backdrop-blur-sm min-h-[7rem]"
                onClick={() => navigate(action.path)}
                data-tour={tourAttr}
              >
                <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2 h-full">
                  <div className={`rounded-lg p-3 ${action.color} transition-colors`}>
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h3 className="font-heading font-bold text-sm text-foreground leading-tight">
                    {action.title}
                  </h3>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}